import isArray from 'lodash/isArray';
import uniqueId from 'lodash/uniqueId';
import Data from '../data';
import Utils from '../utils';
import LogoTypeEnum from './LogoType.enum';
import ThemeTypeEnum from './ThemeType.enum';

const { Session, Validation, Util: CommonUtil } = Utils;
const { Capability } = Data;
class Util {
	// Converts BrandedTrackingConfig to BrandingProperties (SWSIM data model)
	static getBrandingProperties = brandingProperties => ({
		...brandingProperties,
		Toggles: {
			ShowLogo: brandingProperties.LogoType === LogoTypeEnum.Image,
			ShowCustomColors: false,
			ShowSocialMedia:
				brandingProperties.SocialMedia &&
				brandingProperties.SocialMedia.filter(c => !!c.Url).length > 0,
			ShowStoreUrl: !!brandingProperties.LogoTargetUrl,
			ShowMenuLinks:
				brandingProperties.MenuLinks &&
				brandingProperties.MenuLinks.filter(c => !!c.Url).length > 0,
			ShowReturnPolicy: !!brandingProperties.ReturnPolicy,
			ShowEmail: !!brandingProperties.Email,
			ShowPhone: !!brandingProperties.Phone,
			ShowShipmentDetails: true,
			ShowItemImages: true,
		},
	});

	static getBrandedTrackingData = response => {
		const { data: { defaultBrandingId = 0, brandings = [] } = {} } = response;
		const data =
			isArray(brandings) && brandings.length > 0
				? this.getBrandedTrackingConfig(defaultBrandingId, brandings)
				: this.getBrandedTrackingDataDefault();
		return data;
	};

	static getBrandedTrackingDataDefault = () => ({
		brandingId: null,
		brandingProperties: {
			Theme: ThemeTypeEnum.Light,
			BrandName: '',
			LogoTargetUrl: '',
			LogoType: LogoTypeEnum.Image,
			LogoUrl: this.getDefaultLogoUrl(),
			Email: '',
			Phone: '',
			ReturnPolicy: '',
			MenuLinks: [],
			SocialMedia: [],
			LogoName: '',
			TempLogoId: '',
		},
		reference: {
			isActive: false,
			enabledPrints: {
				stores: [],
				otherPrints: true,
			},
		},
	});

	// Converts BrandingProperties (SWSIM data model) to BrandedTrackingConfig
	static getBrandedTrackingConfig = (defaultBrandingId, brandings) => {
		const branding =
			(brandings.length === 1
				? brandings[0]
				: brandings.find(c => c.BrandingId === defaultBrandingId)) ||
			brandings[0];
		const properties = {
			...branding.BrandingProperties,
			LogoName: '',
			TempLogoId: '',
			MenuLinks: branding.BrandingProperties.MenuLinks.map(menu => ({
				...menu,
				key: uniqueId(),
			})),
		};
		return {
			brandingId: branding.BrandingId,
			brandingProperties: properties,
			reference: JSON.parse(branding.Reference),
		};
	};

	static getNotificationSettingProperties = notificationSettingProperties => ({
		...notificationSettingProperties,
		EmailConfig: {
			...notificationSettingProperties.EmailConfig,
			ContentBlocks:
				notificationSettingProperties.EmailConfig &&
				isArray(notificationSettingProperties.EmailConfig.ContentBlocks)
					? notificationSettingProperties.EmailConfig.ContentBlocks.filter(
							x => !!x.Value
						)
					: [],
		},
	});

	static getNotificationSettingsData = response => {
		const {
			data: {
				defaultNotificationSettingId = 0,
				notificationSettings = [],
			} = {},
		} = response;
		const data =
			isArray(notificationSettings) && notificationSettings.length > 0
				? this.getNotificationSettingsConfig(
						defaultNotificationSettingId,
						notificationSettings
					)
				: this.getNotificationSettingsDataDefault(response);
		return data;
	};

	static getNotificationSettingsDataDefault = ({
		fromName = '',
		promotional = '',
		customerEmail = '',
	}) => ({
		notificationSettingId: null,
		notificationSettingProperties: {
			Triggers: [
				{
					NotificationType: 'ShipmentCreated',
					Channels: [{ ChannelType: 'Email' }],
				},
			],
			EmailConfig: {
				ContentBlocks: [
					{
						ContentBlockType: 'Alert',
						Value: promotional,
					},
				],
				FromName: fromName,
				ReplyToEmail: customerEmail,
				BCCEmails: [],
			},
		},
		reference: {
			isActive: false,
			enabledPrints: {
				stores: [],
				otherPrints: true,
			},
		},
	});

	// There is a list of different settings, each settings item has list of properties
	// get just list of these properties for current settings item retrieved by default id
	// Converts NotificationSettings (SWSIM data model) to NotificationSettingsConfig
	static getNotificationSettingsConfig = (
		defaultNotificationSettingId,
		notificationSettings
	) => {
		const notificationSetting =
			(notificationSettings.length === 1
				? notificationSettings[0]
				: notificationSettings.find(
						c => c.NotificationSettingId === defaultNotificationSettingId
					)) || notificationSettings[0];
		const properties = {
			...notificationSetting.NotificationSettingProperties,
		};
		return {
			notificationSettingId: notificationSetting.NotificationSettingId,
			notificationSettingProperties: properties,
			reference: JSON.parse(notificationSetting.Reference),
		};
	};

	static composeLogObject = (
		brandingProperties,
		notificationSettingProperties,
		bccEmailsEnabled,
		storesList,
		reference,
		action
	) => {
		const {
			Theme,
			BrandName,
			LogoTargetUrl,
			LogoType,
			LogoUrl,
			Email,
			Phone,
			ReturnPolicy,
			MenuLinks,
			SocialMedia,
		} = brandingProperties;
		const { Triggers = [] } = notificationSettingProperties;
		const { enabledPrints: { stores = [], otherPrints = [] } = {} } = reference;
		const themeValue = {
			Light: 'blue',
			Dark: 'black_grey',
			Peach: 'peach',
		};
		let publicationStatus = 'not_saved';
		let headerType = 'brand_name';

		if (action && action !== '') {
			publicationStatus = action === 'publish' ? 'published' : 'draft';
		}

		if (LogoUrl === this.getDefaultLogoUrl()) {
			headerType = 'default';
		} else if (LogoUrl && LogoUrl !== '' && LogoType === LogoTypeEnum.Image) {
			headerType = 'logo';
		}

		const storesLogArray = this.isNoStoreConnected(storesList)
			? []
			: stores.map(store => {
					const storeName = storesList
						.find(s => s.StoreID.toString() === store.storeId)
						.StoreName.toLowerCase()
						.replace(/\s+/g, '_');
					return { [storeName]: store.included };
				});
		storesLogArray.push({ all_other_labels: otherPrints });

		const logObject = {
			category: 'branded_tracking_settings_change',
			publication_status: publicationStatus,
			theme: themeValue[Theme],
			brand_name: BrandName,
			brand_url: LogoTargetUrl,
			header_type: headerType,
			logo_uploaded: !!(
				LogoUrl &&
				LogoUrl !== '' &&
				!this.isImageDefaultLogo(LogoUrl)
			),
			menu_links: MenuLinks.map((menu, index) => ({
				[`menu_link_${index + 1}`]: menu.Text,
			})), // menuLinks,
			menu_link_URLs: MenuLinks.map((menu, index) => ({
				[`menu_link_URL_${index + 1}`]: menu.Url,
			})),
			social_links: SocialMedia.map(social => ({
				[social.Type.toLowerCase()]: !!social.Url,
			})),
			phone: Phone && Phone !== '' ? 'REMOVED' : '',
			email: Email && Email !== '' ? 'REMOVED' : '',
			return_policy: ReturnPolicy || '',
			stores: storesLogArray,
			shipment_created: this.canUseBrandedEmails()
				? this.isNotificationTypeSelected(Triggers, 'ShipmentCreated') || false
				: '',
			tracking_available: this.canUseBrandedEmails()
				? this.isNotificationTypeSelected(Triggers, 'DeliveryDateAnnounced') ||
					false
				: '',
			out_for_delivery: this.canUseBrandedEmails()
				? this.isNotificationTypeSelected(Triggers, 'OutForDelivery') || false
				: '',
			delivered: this.canUseBrandedEmails()
				? this.isNotificationTypeSelected(Triggers, 'Delivered') || false
				: '',
			send_a_copy: this.canUseBrandedEmails() ? bccEmailsEnabled || false : '',
		};
		return logObject;
	};

	static isNotificationTypeSelected = (triggers, notificationType) => {
		if (!this.canUseBrandedEmails()) {
			return null;
		} else {
			return (
				isArray(triggers) &&
				triggers.some(x => x.NotificationType === notificationType)
			);
		}
	};

	static isLogoInvalid = (logoType, logoUrl) =>
		logoType === LogoTypeEnum.Image && (logoUrl || '') === '';

	static isLogoTargetUrlInvalid = storeUrl =>
		(storeUrl || '') !== '' && !Validation.isValidUrl(storeUrl);

	static isMenuLinksInvalid = menuLinks =>
		menuLinks && menuLinks.some(this.isMenuLinkInvalid);

	static isMenuLinkInvalid = menuLink =>
		this.isMenuUrlInvalid(menuLink) || this.isMenuTextInvalid(menuLink);

	static isMenuTextInvalid = menuLink =>
		(menuLink.Url || '') !== '' && (menuLink.Text || '') === '';

	static isMenuUrlInvalid = menuLink => {
		if ((menuLink.Text || '') !== '') {
			return !Validation.isValidUrl(menuLink.Url);
		} else {
			return (
				(menuLink.Url || '') !== '' && !Validation.isValidUrl(menuLink.Url)
			);
		}
	};

	static getUrlValidationError = url =>
		url === ''
			? 'Link is required'
			: 'Invalid URL. Please enter a full website URL, such as: http://www.example.com';

	static getDefaultLogoUrl = () =>
		'https://resources.stamps.com/logos/logo_stamps_red.png';

	static openLearningCenterArticle = () => {
		CommonUtil.openHelpCenter('21289815567515');
	};

	static canUseBrandedEmails = () =>
		Session.hasCapability(Capability.BrandedEmails);

	static isFileTypeValid = name =>
		name &&
		(name.toLowerCase().indexOf('.png') !== -1 ||
			name.toLowerCase().indexOf('.jpg') !== -1 ||
			name.toLowerCase().indexOf('.gif') !== -1 ||
			name.toLowerCase().indexOf('.jpeg') !== -1);

	static isFileSizeValid = size => size && size < 1024 * 1024 * 2;

	static isImageDefaultLogo = logoUrl => logoUrl === this.getDefaultLogoUrl();

	// Return `true` if store name is 'amazon'
	static isAmazonStore = name =>
		!!name && name.toLowerCase().indexOf('amazon') >= 0;

	// Return true for amazon stores
	static isStoreDisabled = store => this.isAmazonStore(store.Marketplace.Name);

	static isNoStoreConnected = lookupStoresList =>
		!isArray(lookupStoresList) || lookupStoresList.length <= 1;
}

export default Util;
